import { createApi } from '@reduxjs/toolkit/query/react';
import { TAG_TYPES } from '../constants/queryTags';
import baseQueryWithTimeout from './baseQueryWithTimeout';
import { clearKnownPii, registerKnownPii } from '../services/gtmService/piiRegistry';

const { PET } = TAG_TYPES;

export const policyApi = createApi({
  reducerPath: 'policyApi',
  baseQuery: baseQueryWithTimeout,
  tagTypes: [PET],
  endpoints: (builder) => ({
    // Get
    getPetInfo: builder.query({
      query: (auth0Token) => {
        return {
          url: '/api/v3/policy/petinfo',
          method: 'GET',
          headers: {
            customAuthToken: auth0Token
          }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled;
          // Skip if this response belongs to a stale token (auth changed mid-flight).
          if (arg !== getState()?.authentication?.authDetails?.auth0AccessToken) return;
          clearKnownPii();
          data?.forEach((pet) => {
            registerKnownPii(pet?.Name);
            registerKnownPii(pet?.PolicyDetails?.PolicyHolder?.firstName);
            registerKnownPii(pet?.PolicyDetails?.PolicyHolder?.lastName);
          });
        } catch {
          // query failed; nothing to register
        }
      },
      providesTags: [PET]
    }),
    getPolicyVersions: builder.query({
      query: (auth0Token) => {
        return {
          url: '/api/v3/policy/product-version',
          method: 'GET',
          headers: {
            customAuthToken: auth0Token
          }
        };
      }
    }),
    getContactDetails: builder.query({
      query: (data) => ({
        url: `/api/v3/policy/${data.policyNumber}/contactdetails`,
        headers: {
          customAuthToken: data.auth0Token
        }
      })
    }),
    // Post
    getPremiumPayment: builder.mutation({
      query: ({ data, auth0Token }) => ({
        url: '/api/v3/policy/premiumpayment',
        method: 'POST',
        headers: { customAuthToken: auth0Token },
        body: data
      })
    }),
    updateContactDetails: builder.mutation({
      query: (data) => ({
        url: '/api/policy/updateContactDetails',
        method: 'POST',
        body: data
      }),
      invalidatesTags: [PET]
    }),
    updatePreferredContact: builder.mutation({
      query: (data) => ({
        url: '/api/policy/updatePreferredContact',
        method: 'POST',
        body: data
      })
    }),
    updateReimbursementInformation: builder.mutation({
      query: ({ data, policyNumber, isPetsureUnderwriter }) => ({
        url: `/api/policy/ReimbursementInformation/${policyNumber}/${isPetsureUnderwriter}`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [PET]
    }),
    updatePremiumPaymentDetails: builder.mutation({
      query: ({ data, policyNumber, isPetsureUnderwriter }) => ({
        url: `/api/policy/${policyNumber}/${isPetsureUnderwriter}/BankingInformation`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [PET]
    })
  })
});

export const {
  useGetPetInfoQuery,
  useGetContactDetailsQuery,
  useGetPremiumPaymentMutation,
  useUpdateContactDetailsMutation,
  useUpdatePreferredContactMutation,
  useUpdateReimbursementInformationMutation,
  useUpdatePremiumPaymentDetailsMutation,
  useLazyGetPolicyVersionsQuery
} = policyApi;
